import { UILabel, UITableView, UITableViewDatasource, UITableViewDelegate, UITableViewCell, UIView, UIViewController, UIApplication } from "uikit";
import { IndexPath } from "foundation";
import { DLDB } from "dldb";
import { StepView, BookingStep } from "./StepView";
import { BookingEventCell } from "./BookingEventCell";
import { AppDelegate } from "../AppDelegate";

export interface BookingEventProtocolDelegate
{
    onEventDidChange ( vc: BookingEventViewController, event: any ) : void ;
}

/**
 * Events step: lists the venue events open for online booking (VenueEvent) and lets the client pick
 * one. The selection goes to the cart (cart.event), which in turn fixes the booking day and the
 * preset the product step lists — so no calendar step is needed for event venues.
 */
export class BookingEventViewController extends UIViewController implements UITableViewDatasource, UITableViewDelegate, BookingStep
{
    delegate : BookingEventProtocolDelegate = null ;

    stepView: StepView;

    private spinLoaderView : UIView;
    private tableView      : UITableView;
    private noEventsLabel  : UILabel;
    private errorEventLabel: UILabel;

    private events        : any[] = [];
    private selectedIndex : number = -1;
    errors                : string[] = [];

    viewDidLoad ( )
    {
        super.viewDidLoad();

        this.tableView.dataSource = this;
        this.tableView.delegate   = this;

        if ( this.noEventsLabel ) this.noEventsLabel.hidden = true;
        this.setErrors( [] );

        this.fetchEvents();
    }

    get selectedEvent ( ) : any | null {
        return this.selectedIndex >= 0 ? this.events[ this.selectedIndex ] : null;
    }

    fetchEvents ( )
    {
        if ( this.spinLoaderView ) this.spinLoaderView.hidden = false;

        DLDB.shared.events( ( code, res ) => {
            if ( this.spinLoaderView ) this.spinLoaderView.hidden = true;

            this.events = ( code === 200 && res.data != null ) ? res.data : [];
            this.selectedIndex = -1;

            if ( this.noEventsLabel ) this.noEventsLabel.hidden = this.events.length > 0;
            this.tableView.reloadData();
        } );
    }

    // TABLE VIEW
    numberOfSections ( tableView: UITableView ) { return 1; }

    numberOfRowsInSection ( tableView: UITableView, section: number ) : number { return this.events.length; }

    cellForRowAt ( tableView: UITableView, indexPath: IndexPath ) : UITableViewCell
    {
        let cell = tableView.dequeueReusableCellWithIdentifier( "EventCell" ) as BookingEventCell;
        cell.event = this.events[ indexPath.row ];
        cell.isSelected = ( indexPath.row === this.selectedIndex );
        return cell;
    }

    didSelectCellAtIndexPath ( tableView: UITableView, indexPath: IndexPath )
    {
        if ( indexPath.row === this.selectedIndex ) return;

        if ( this.selectedIndex >= 0 ) {
            const previous = tableView.cellAtIndexPath( IndexPath.indexForRowInSection( this.selectedIndex, 0 ) );
            if ( previous ) previous.isSelected = false;
        }

        this.selectedIndex = indexPath.row;
        const cell = tableView.cellAtIndexPath( indexPath );
        if ( cell ) cell.isSelected = true;

        this.selectEvent( this.events[ indexPath.row ] );
    }

    private selectEvent ( event: any )
    {
        this.clearError( "event" );
        DLDB.shared.cart().event = event;
        this.delegate?.onEventDidChange( this, event );
    }

    public clear ( )
    {
        this.selectedIndex = -1;
        DLDB.shared.cart().event = null;
        this.setErrors( [] );
        this.tableView.reloadData();
    }

    check ( ) : boolean
    {
        const valid = this.selectedEvent != null;
        if ( !valid ) this.errors.push( "event" );

        if ( !(UIApplication.shared.delegate as AppDelegate).dryRunCheck ) this.setErrors( this.errors );

        return valid;
    }

    hasValue ( ) : boolean { return this.selectedEvent != null; }

    setErrors ( errors: string[] )
    {
        const errorSpec = this.errorSpec( );

        for ( const err in errorSpec )
            this.clearError( err );

        for ( const err of errors )
            if ( err in errorSpec )
                errorSpec[ err ].hidden = false;
    }

    clearError ( error: string )
    {
        const errorSpec = this.errorSpec( );

        this.errors = this.errors.filter( e => e !== error );

        if ( errorSpec.hasOwnProperty( error ) )
            errorSpec[ error ].hidden = true;
    }

    private errorSpec ( )
    {
        let errorSpec = {};
        if ( this.errorEventLabel ) errorSpec[ "event" ] = this.errorEventLabel;
        return errorSpec;
    }
}
